import { pluralize } from '@travelata-libs/utils';
import _ from 'underscore';
import { MAX_PREPAYMENTS } from '@/js/common/Constants/globalParams';
import TourFactoryOptimize from './tourProductOptimize/TourFactoryOptimize';

const PRODUCT_DEFAULTS = {
    id: null,
    type: null,
    price: null,
    discount: null,
    description: null,
    hash: null,
    firstPayment: 1,
    medicalInsurance: null,
    transferType: null,
    tourCriteria: null,
    actualizeInProgress: false,
    firstPaymentDefinition: null,
    isExclusive: false,
    nights: null,
    transferDescription: null,
    nightsInHotel: null,
    tour: null,
    hotelOffer: null,
    railwayTour: null,
    similarProducts: [],
};

export default class TourProductFactoryOptimize {
    constructor(data, resorts) {

        this.cid = _.uniqueId('c');

        Object.assign(this, PRODUCT_DEFAULTS);
        this.similarProducts = [];

        let element = data.medicalInsurance;
        if (element !== undefined) {
            this.medicalInsurance = element;
        }

        element = data.tourCriteria;
        if (element !== undefined) {
            this.tourCriteria = element;
        }
        element = data.price;
        if (element !== undefined) {
            this.price = element;
        }

        element = data.discount;
        if (element !== undefined) {
            this.discount = element;
        }

        element = data.hotelAvailable;
        if (element !== undefined) {
            this.hotelAvailable = element;
        }

        this.type = 'TourProduct';

        element = data?.tour?.id || null;
        if (element !== undefined) {
            this.id = element;
        }

        element = data.hash;
        if (element !== undefined) {
            this.hash = element;
        }

        element = data.tour;
        if (element !== undefined) {
            this.tour = new TourFactoryOptimize(data.tour, resorts);
        }

        element = data.transferType;
        if (element !== undefined) {
            this.transferType = element;
        }

        element = data.firstPaymentDefinition;
        if (element !== undefined) {
            this.firstPaymentDefinition = element;
        }

        element = data.firstPaymentDefinitions;
        if (element !== undefined) {
            this.firstPaymentDefinitions = element?.slice(0, MAX_PREPAYMENTS);
        }

        element = data.allFirstPaymentDefinitions;
        if (element !== undefined) {
            this.allFirstPaymentDefinitions = element?.slice(0, MAX_PREPAYMENTS);
        }

        element = data.availableTransfers;
        if (element !== undefined) {
            this.tour.set('availableTransfers', element);
        }

        if (data.tour?.nights?.tour) {
            this.nights = data.tour.nights.tour;
        }

        if (data.tour?.nights?.hotel) {
            this.nightsInHotel = data.tour.nights.hotel;
        }

        this.transferDescription = data.tour?.transferDescription || null;
    }


    get product() {
        return this.tour || this.hotelOffer || this.railwayTour || null;
    }

    get tourProducts() {
        return _.filter(this.similarProducts, (similarProduct) => similarProduct.get('type') === 'TourProduct');
    }

    get hotelOfferProducts() {
        return _.filter(this.similarProducts, (similarProduct) => similarProduct.get('type') === 'HotelOfferProduct');
    }

    get railwayProducts() {
        return _.filter(this.similarProducts, (similarProduct) => similarProduct.get('type') === 'RailwayTourProduct');
    }

    get cheapestTourProduct() {
        if (this.type === 'TourProduct') {
            return this;
        }
        return this.tourProducts?.[0] || null;
    }

    get cheapestHotelOfferProduct() {
        if (this.type === 'HotelOfferProduct') {
            return this;
        }
        return this.hotelOfferProducts?.[0] || null;
    }

    get cheapestRailwayProduct() {
        if (this.type === 'RailwayTourProduct') {
            return this;
        }
        return this.railwayProducts?.[0] || null;
    }

    get roomsTouristGroup() {
        return this.product.get('roomGroups')?.reduce(
            (acc, { touristGroup }) => ({
                adults: acc.adults + touristGroup.adults,
                kids: acc.kids + touristGroup.kids,
                infants: acc.infants + touristGroup.infants,
                kidsAges: [...acc.kidsAges, ...(touristGroup.kidsAges || [])],
            }),
            {
                adults: 0, kids: 0, kidsAges: [], infants: 0,
            },
        );
    }

    get touristText() {
        const { adults, kids, infants } = this.roomsTouristGroup;
        const touristCount = adults + kids + infants;
        const touristText = pluralize(['туриста', 'туристов', 'туристов'], touristCount);
        const roomsText = pluralize(['номер', 'номера', 'номеров'], this.product.get('roomGroups').length);
        return `${roomsText} для ${touristText}`;
    }

    get roomsPriceText() {
        const roomsLength = this.product.get('roomGroups').length;
        const text = pluralize(['номер', 'номера', 'номеров'], roomsLength, false);
        return `Цена за ${roomsLength} ${text}`;
    }

    get dateFrom() {
        return this.product.get('checkInDateRange').get('from').getTime();
    }

    get freeCancellationHotelOffer() {
        return !!this.getCancellationPolicy(this.hotelOffer)?.freeCancellationDateBefore
            || _.some(
                this.hotelOfferProducts,
                (similarHotelOffer) => !!this.getCancellationPolicy(similarHotelOffer)?.freeCancellationDateBefore,
            );
    }

    getCancellationPolicy(product) {
        return (product?.get('hotelOffer') || product)?.get('cancellationPolicy');
    }


    get(field) {
        return this[field];
    }

    set(field, value) {
        if (field !== null && typeof field === 'object') {
            Object.keys(field).forEach((key) => {
                if (key === 'cid' || key === 'collection') {
                    return;
                }
                this[key] = field[key];
            });
            return this;
        }
        this[field] = value;
        return this;
    }

    get attributes() {
        return this;
    }

    toJSON() {
        const result = {};
        Object.keys(this).forEach((key) => {
            if (key === 'cid' || key === 'collection') {
                return;
            }
            result[key] = this[key];
        });
        return result;
    }

    on() {
        return this;
    }

    off() {
        return this;
    }

    trigger() {
        return this;
    }

    hasChanged() {
        return true;
    }

    previousAttributes() {
        return {};
    }
}
